import React, { useRef, useState, useEffect, useCallback } from 'react';
import { TagIcon, TrashIcon, InformationCircleIcon, ArrowLeftIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { useConfirm } from '../../contexts/ConfirmContext';

const SablonKarti = ({ sablon, onClick, onDelete, isBack, onToggleBack }) => {
  const ozetRef = useRef(null);
  const rafRef = useRef(null);
  const posRef = useRef(0); // birikimli kaydırma konumu (alt-piksel yuvarlamasına dayanıklı)
  const [tasmaVar, setTasmaVar] = useState(false); // özet kutusunda taşma var mı
  const [altta, setAltta] = useState(false);       // en alta kaydırıldı mı
  const confirm = useConfirm();

  const temizleVeKisalt = (metin, uzunluk) => {
    if (!metin) return 'İçerik yok';
    const temizMetin = metin
      .replace(/<[^>]*>/g, '') // HTML etiketlerini kaldır
      .replace(/&nbsp;/g, ' ') // &nbsp; karakterlerini boşlukla değiştir
      .replace(/\s+/g, ' ') // Birden fazla boşluğu tek boşluğa indir
      .trim();
    return temizMetin.length > uzunluk ? temizMetin.slice(0, uzunluk) + '...' : temizMetin;
  };

  const kisaltilmisBaslik = temizleVeKisalt(sablon.baslik, 130);

  const getSablonKategorileri = () => {
    if (!sablon.kategoriler || !Array.isArray(sablon.kategoriler) || sablon.kategoriler.length === 0) return 'Belirtilmemiş';
    return sablon.kategoriler.map(kategori => kategori.isim).join(', ');
  };

  // Taşma ve "en altta mı" durumunu ölç
  const olcumGuncelle = useCallback(() => {
    const el = ozetRef.current;
    if (!el) return;
    setTasmaVar(el.scrollHeight > el.clientHeight + 1);
    setAltta(el.scrollTop + el.clientHeight >= el.scrollHeight - 1);
  }, []);

  useEffect(() => {
    olcumGuncelle();
    window.addEventListener('resize', olcumGuncelle);
    return () => window.removeEventListener('resize', olcumGuncelle);
  }, [olcumGuncelle, sablon.ozet]);

  const durdurKaydirma = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  const baslatKaydirma = useCallback(() => {
    const el = ozetRef.current;
    if (!el) return;
    durdurKaydirma();
    posRef.current = el.scrollTop;
    const hiz = 1.2; // her karede px
    const adim = () => {
      const e = ozetRef.current;
      if (!e) return;
      const maks = e.scrollHeight - e.clientHeight;
      posRef.current = Math.min(posRef.current + hiz, maks);
      e.scrollTop = posRef.current;
      if (posRef.current >= maks - 0.5) {
        setAltta(true);
        durdurKaydirma();
        return;
      }
      rafRef.current = requestAnimationFrame(adim);
    };
    rafRef.current = requestAnimationFrame(adim);
  }, [durdurKaydirma]);

  // Karttan ayrılınca otomatik olarak başa dön — yalnızca gerçek hover'lı (masaüstü) cihazlarda
  const basaDon = useCallback(() => {
    durdurKaydirma();
    const hoverVar = typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(hover: hover)').matches
      : true;
    if (!hoverVar) return; // dokunmatik cihazlarda sıfırlama yapma
    const el = ozetRef.current;
    if (el) el.scrollTo({ top: 0, behavior: 'smooth' });
  }, [durdurKaydirma]);

  useEffect(() => durdurKaydirma, [durdurKaydirma]); // unmount temizliği

  const acEditor = () => onClick(sablon);
  const cevir = (e) => {
    e.stopPropagation();
    onToggleBack(sablon.id);
  };
  const klavyeAc = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      acEditor();
    }
  };

  return (
    <div className="group relative h-80 lg:h-[23rem] 2xl:h-[26rem] [perspective:1600px]">
      <div
        className={`relative h-full w-full transition-transform duration-500 ease-out [transform-style:preserve-3d] ${
          isBack ? '[transform:rotateY(180deg)]' : ''
        }`}
      >
        {/* ÖN YÜZ */}
        <div
          className="absolute inset-0 flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-lg transition-all duration-200 [backface-visibility:hidden] cursor-pointer group-hover:-translate-y-0.5 group-hover:border-amber-300/50 group-hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2"
          role="button"
          tabIndex={isBack ? -1 : 0}
          onClick={acEditor}
          onKeyDown={klavyeAc}
          onMouseLeave={basaDon}
        >
          <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-300/70 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
          <h3 className="mb-3 line-clamp-3 shrink-0 font-display text-lg font-semibold leading-snug text-slate-800">
            {kisaltilmisBaslik}
          </h3>
          <div className="relative flex-grow overflow-hidden rounded-xl border border-slate-200 bg-slate-50/70">
            <div
              ref={ozetRef}
              onScroll={olcumGuncelle}
              className="scrollbar-hidden absolute inset-0 overflow-y-auto p-3 text-sm leading-relaxed text-slate-600"
            >
              {temizleVeKisalt(sablon.ozet, 600)}
            </div>
            {/* Kaydırma çubuğu yerine yumuşak solma maskesi (alt kısımda içerik varsa) */}
            {tasmaVar && !altta && (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 rounded-b-xl bg-gradient-to-t from-slate-50 via-slate-50/85 to-transparent" />
            )}
            {/* İnce "aşağı kaydır" düğmesi — masaüstünde hover'da, mobilde her zaman görünür */}
            {tasmaVar && !altta && (
              <button
                type="button"
                aria-label="Özeti kaydırarak gör"
                onClick={(e) => e.stopPropagation()}
                onMouseEnter={baslatKaydirma}
                onMouseLeave={durdurKaydirma}
                onTouchStart={(e) => { e.stopPropagation(); baslatKaydirma(); }}
                onTouchEnd={durdurKaydirma}
                className="absolute bottom-2 left-1/2 flex h-7 w-7 -translate-x-1/2 items-center justify-center rounded-full border border-slate-200 bg-white/90 text-slate-500 shadow-sm backdrop-blur-sm transition-all duration-200 hover:border-amber-300/60 hover:text-amber-600 opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
              >
                <ChevronDownIcon className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="mt-3 flex shrink-0 items-center justify-end border-t border-slate-100 pt-3">
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:border-amber-300/60 hover:bg-amber-50 hover:text-amber-700"
              aria-label="Şablon ayrıntılarını göster"
              onClick={cevir}
            >
              <InformationCircleIcon className="h-4 w-4" />
              Detaylar
            </button>
          </div>
        </div>

        {/* ARKA YÜZ */}
        <div
          className="absolute inset-0 flex flex-col overflow-hidden rounded-2xl border border-amber-300/40 bg-white p-6 shadow-lg [backface-visibility:hidden] [transform:rotateY(180deg)] cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2"
          role="button"
          tabIndex={isBack ? 0 : -1}
          onClick={acEditor}
          onKeyDown={klavyeAc}
        >
          <div className="scrollbar-slim flex-grow space-y-3 overflow-y-auto pr-1">
            <div>
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Kategoriler</h4>
              <span className="app-chip">{getSablonKategorileri()}</span>
            </div>
            <div>
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Kanun Maddeleri</h4>
              <div className="flex flex-wrap gap-1.5">
                {Array.isArray(sablon.kanun_maddeleri) && sablon.kanun_maddeleri.length > 0 ? (
                  sablon.kanun_maddeleri.map((madde, i) => (
                    <span key={`${typeof madde === 'string' ? madde : madde.value}-${i}`} className="app-chip app-chip-accent app-num">
                      {typeof madde === 'string' ? madde : madde.label}
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-slate-400">Belirtilmemiş</span>
                )}
              </div>
            </div>
            <div>
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Etiketler</h4>
              <div className="flex flex-wrap gap-1.5">
                {Array.isArray(sablon.etiketler) && sablon.etiketler.length > 0 ? (
                  sablon.etiketler.map((etiket, i) => (
                    <span key={`${typeof etiket === 'string' ? etiket : etiket.value}-${i}`} className="app-chip">
                      <TagIcon className="h-3 w-3" />
                      {typeof etiket === 'string' ? etiket : etiket.label}
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-slate-400">Belirtilmemiş</span>
                )}
              </div>
            </div>
          </div>

          <div className="mt-3 flex shrink-0 items-center justify-between border-t border-slate-100 pt-3">
            <button
              type="button"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-b from-rose-500 to-red-700 text-white shadow-sm transition-transform hover:-translate-y-0.5"
              aria-label="Şablonu sil"
              onClick={async (e) => {
                e.stopPropagation();
                const onaylandi = await confirm({
                  title: 'Şablonu sil',
                  message: `"${sablon.baslik || 'Bu şablon'}" kalıcı olarak silinecek. Bu işlem geri alınamaz.`,
                  confirmText: 'Sil',
                  cancelText: 'Vazgeç',
                  tone: 'danger',
                });
                if (onaylandi) {
                  onDelete(sablon.id);
                }
              }}
            >
              <TrashIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-800"
              aria-label="Karta geri dön"
              onClick={cevir}
            >
              <ArrowLeftIcon className="h-3.5 w-3.5" />
              Geri
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SablonKarti;
